import React, { useState } from 'react';
import {
    Box,
    Card,
    CardContent,
    Grid,
    Stack,
    Typography,
    Paper,
    Chip,
    Divider,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Fade,
    Avatar,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
    ExpandMore as ExpandMoreIcon,
    Person as PersonIcon,
    Phone as PhoneIcon,
    HourglassEmpty as PendingIcon,
    Description as DescriptionIcon,
    Chat as ChatIcon,
    SmartToy as BotIcon,
} from '@mui/icons-material';

interface TranscriptMessage {
    role: 'assistant' | 'user' | 'bot';
    message: string;
    timestamp?: number | null;
}

interface TranscriptChatProps {
    transcript: TranscriptMessage[] | string;
    candidateName: string;
}

// Parse text transcript format like "Bot (Laura): message User: message Bot: message"
function parseTextTranscript(text: string): TranscriptMessage[] {
    if (!text || typeof text !== 'string') return [];

    const messages: TranscriptMessage[] = [];

    // Simple approach: split by speaker patterns and capture both speaker and message
    // Pattern matches "Bot (Laura):", "Bot:", "User:", "Assistant:", "Laura:"
    const regex = /(Bot(?:\s*\([^)]*\))?|User|Assistant|Laura):\s*/gi;

    // Find all matches and their positions
    const speakerPositions: { speaker: string; startIndex: number; messageStart: number }[] = [];
    let match;

    while ((match = regex.exec(text)) !== null) {
        speakerPositions.push({
            speaker: match[1].replace(/\s*\([^)]*\)/i, '').toLowerCase(), // Remove parenthetical like "(Laura)"
            startIndex: match.index,
            messageStart: match.index + match[0].length
        });
    }

    console.log('Speaker positions found:', speakerPositions);

    // Extract messages between speaker positions
    for (let i = 0; i < speakerPositions.length; i++) {
        const current = speakerPositions[i];
        const next = speakerPositions[i + 1];

        // Message ends at the start of the next speaker, or end of text
        const messageEnd = next ? next.startIndex : text.length;
        const message = text.substring(current.messageStart, messageEnd).trim();

        if (message) {
            const isBot = current.speaker === 'bot' || current.speaker === 'assistant' || current.speaker === 'laura';
            messages.push({
                role: isBot ? 'assistant' : 'user',
                message: message,
                timestamp: null,
            });
        }
    }

    console.log('Parsed transcript result:', { originalLength: text.length, messageCount: messages.length, messages });

    return messages;
}

function TranscriptChat({ transcript, candidateName }: TranscriptChatProps) {
    // Handle both array and string formats
    let messages: TranscriptMessage[] = [];

    console.log('TranscriptChat received:', {
        type: typeof transcript,
        isArray: Array.isArray(transcript),
        length: Array.isArray(transcript) ? transcript.length : (typeof transcript === 'string' ? transcript.length : 0),
        sample: Array.isArray(transcript) ? transcript.slice(0, 3) : (transcript as string)?.substring?.(0, 200),
        roles: Array.isArray(transcript) ? Array.from(new Set(transcript.map((m: any) => m.role))) : 'N/A'
    });

    if (Array.isArray(transcript)) {
        // Normalize roles in the array
        messages = transcript.map((msg: any): TranscriptMessage => {
            const role = msg.role?.toLowerCase();
            return {
                role: (role === 'bot' || role === 'assistant') ? 'assistant' : 'user',
                message: msg.message || msg.content || msg.text || '',
                timestamp: msg.timestamp || null,
            };
        }).filter((msg) => msg.message);
    } else if (typeof transcript === 'string') {
        messages = parseTextTranscript(transcript);
    }

    console.log('TranscriptChat processed messages:', { count: messages.length, messages });

    if (!messages || messages.length === 0) {
        return (
            <Box sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>
                <ChatIcon sx={{ fontSize: 32, mb: 1, opacity: 0.4 }} />
                <Typography variant="body2">No transcript available for this interview.</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{
            maxHeight: 400,
            overflowY: 'auto',
            p: 2,
            bgcolor: alpha('#f5f5f5', 0.5),
            borderRadius: 2,
        }}>
            <Stack spacing={2}>
                {messages.map((msg, index) => {
                    // Handle both 'assistant' and 'bot' roles as interviewer
                    const isInterviewer = msg.role === 'assistant' || msg.role === 'bot';
                    const displayName = isInterviewer ? 'Interviewer' : candidateName || 'Candidate';

                    return (
                        <Box
                            key={index}
                            sx={{
                                display: 'flex',
                                flexDirection: isInterviewer ? 'row' : 'row-reverse',
                                gap: 1.5,
                                alignItems: 'flex-start',
                            }}
                        >
                            <Avatar
                                sx={{
                                    width: 36,
                                    height: 36,
                                    bgcolor: isInterviewer
                                        ? 'primary.main'
                                        : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                    background: isInterviewer
                                        ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                                        : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                    fontSize: 14,
                                }}
                            >
                                {isInterviewer ? <BotIcon sx={{ fontSize: 20 }} /> : displayName[0]?.toUpperCase() || 'C'}
                            </Avatar>
                            <Box sx={{ maxWidth: '75%' }}>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        fontWeight: 600,
                                        color: isInterviewer ? 'primary.main' : '#10b981',
                                        display: 'block',
                                        mb: 0.5,
                                        textAlign: isInterviewer ? 'left' : 'right',
                                    }}
                                >
                                    {displayName}
                                </Typography>
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: 1.5,
                                        borderRadius: 2,
                                        bgcolor: isInterviewer ? 'white' : alpha('#10b981', 0.1),
                                        border: `1px solid ${isInterviewer ? alpha('#667eea', 0.2) : alpha('#10b981', 0.2)}`,
                                    }}
                                >
                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                        {msg.message}
                                    </Typography>
                                </Paper>
                            </Box>
                        </Box>
                    );
                })}
            </Stack>
        </Box>
    );
}

interface InterviewResultsTabProps {
    candidates: any[];
    selectedCandidateId?: string | null;
}

const resultLabels: Record<string, string> = {
    greetingMotivation: 'Greeting & Motivation',
    companyIntro: 'Company Intro',
    valuesGrowth: 'Values & Growth',
    travelCommunication: 'Travel & Communication',
    projectExperience: 'Project Experience',
    dataUsage: 'Data Usage',
    toolsTechnical: 'Tools & Technical Proficiency',
    processNextStep: 'Process / Next Step',
    salaryExpectations: 'Salary Expectations',
    overview: 'Overview',
    transcript: 'Transcript',
};

export default function InterviewResultsTab({ candidates, selectedCandidateId }: InterviewResultsTabProps) {
    const [expandedId, setExpandedId] = useState<string | null>(null);
    const completedCandidates = candidates.filter(c => c.interviewResults || c.status === 'completed');
    const pendingCandidates = candidates.filter(c => c.status === 'pending' || (!c.interviewResults && c.status !== 'completed'));

    // Auto-expand the accordion when redirected from Link Details
    React.useEffect(() => {
        if (selectedCandidateId) {
            setExpandedId(selectedCandidateId);
        }
    }, [selectedCandidateId]);

    return (
        <Fade in timeout={400}>
            <Box>
                {/* Stats Cards */}
                <Grid container spacing={3} sx={{ mb: 4 }}>
                    <Grid item xs={12} sm={4}>
                        <Card sx={{
                            background: alpha('#10b981', 0.1),
                            border: `1px solid ${alpha('#10b981', 0.2)}`,
                        }}>
                            <CardContent sx={{ p: 3 }}>
                                <Stack direction="row" alignItems="center" justifyContent="space-between">
                                    <Box>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                            Completed Interviews
                                        </Typography>
                                        <Typography variant="h3" sx={{ color: '#10b981', fontWeight: 700 }}>
                                            {completedCandidates.length}
                                        </Typography>
                                    </Box>
                                    <Box sx={{
                                        width: 48,
                                        height: 48,
                                        borderRadius: 2,
                                        bgcolor: 'white',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: '#10b981',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                                    }}>
                                        <PhoneIcon />
                                    </Box>
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Card sx={{
                            background: alpha('#f59e0b', 0.1),
                            border: `1px solid ${alpha('#f59e0b', 0.2)}`,
                        }}>
                            <CardContent sx={{ p: 3 }}>
                                <Stack direction="row" alignItems="center" justifyContent="space-between">
                                    <Box>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                            Pending Interviews
                                        </Typography>
                                        <Typography variant="h3" sx={{ color: '#f59e0b', fontWeight: 700 }}>
                                            {pendingCandidates.length}
                                        </Typography>
                                    </Box>
                                    <Box sx={{
                                        width: 48,
                                        height: 48,
                                        borderRadius: 2,
                                        bgcolor: 'white',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: '#f59e0b',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                                    }}>
                                        <PendingIcon />
                                    </Box>
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Card sx={{
                            background: alpha('#667eea', 0.1),
                            border: `1px solid ${alpha('#667eea', 0.2)}`,
                        }}>
                            <CardContent sx={{ p: 3 }}>
                                <Stack direction="row" alignItems="center" justifyContent="space-between">
                                    <Box>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                            Total Candidates
                                        </Typography>
                                        <Typography variant="h3" sx={{ color: '#667eea', fontWeight: 700 }}>
                                            {candidates.length}
                                        </Typography>
                                    </Box>
                                    <Box sx={{
                                        width: 48,
                                        height: 48,
                                        borderRadius: 2,
                                        bgcolor: 'white',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: '#667eea',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                                    }}>
                                        <PersonIcon />
                                    </Box>
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                {/* Interview Results List */}
                <Card>
                    <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                        <Typography variant="h6" sx={{ mb: 3 }}>
                            <DescriptionIcon sx={{ mr: 1, verticalAlign: 'middle', color: 'primary.main' }} />
                            Interview Results
                        </Typography>

                        {completedCandidates.length === 0 ? (
                            <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
                                <PhoneIcon sx={{ fontSize: 48, mb: 2, opacity: 0.4 }} />
                                <Typography>No completed interviews yet.</Typography>
                                <Typography variant="body2" sx={{ mt: 1 }}>
                                    Results will appear here after candidates complete their phone interviews.
                                </Typography>
                            </Box>
                        ) : (
                            <Stack spacing={2}>
                                {completedCandidates.map((candidate, i) => {
                                    const candidateKey = candidate.candidateId || `idx-${i}`;
                                    return (
                                        <Accordion
                                            key={candidateKey}
                                            expanded={expandedId === candidateKey}
                                            onChange={(_, isExpanded) => setExpandedId(isExpanded ? candidateKey : null)}
                                            sx={{
                                                borderRadius: 2,
                                                '&:before': { display: 'none' },
                                                boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                                            }}
                                        >
                                            <AccordionSummary
                                                expandIcon={<ExpandMoreIcon />}
                                                sx={{
                                                    '&:hover': { bgcolor: 'action.hover' },
                                                }}
                                            >
                                                <Stack direction="row" alignItems="center" spacing={2} sx={{ width: '100%', pr: 2 }}>
                                                    <Box sx={{
                                                        width: 40,
                                                        height: 40,
                                                        borderRadius: '50%',
                                                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        color: 'white',
                                                        fontWeight: 700,
                                                        fontSize: 16,
                                                    }}>
                                                        {(candidate.candidateName || 'U')[0].toUpperCase()}
                                                    </Box>
                                                    <Box sx={{ flex: 1 }}>
                                                        <Typography fontWeight={600}>
                                                            {candidate.candidateName || 'Unknown Candidate'}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {candidate.candidateEmail || ''}
                                                        </Typography>
                                                    </Box>
                                                    <Stack direction="row" spacing={1} alignItems="center">
                                                        <Chip
                                                            label="Completed"
                                                            size="small"
                                                            color="success"
                                                            variant="outlined"
                                                        />
                                                        {candidate.callMetadata?.callDuration && (
                                                            <Chip
                                                                label={`${Math.round(candidate.callMetadata.callDuration / 60)}m`}
                                                                size="small"
                                                                variant="outlined"
                                                            />
                                                        )}
                                                    </Stack>
                                                </Stack>
                                            </AccordionSummary>
                                            <AccordionDetails sx={{ pt: 0 }}>
                                                <Divider sx={{ mb: 2 }} />
                                                <Grid container spacing={2}>
                                                    {candidate.interviewResults && Object.entries(candidate.interviewResults).map(([key, value]) => {
                                                        // Skip transcript - it's displayed below as a chat
                                                        if (!value || key === 'transcript') return null;
                                                        return (
                                                            <Grid item xs={12} md={6} key={key}>
                                                                <Paper
                                                                    variant="outlined"
                                                                    sx={{
                                                                        p: 2,
                                                                        borderRadius: 2,
                                                                        height: '100%',
                                                                    }}
                                                                >
                                                                    <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>
                                                                        {resultLabels[key] || key}
                                                                    </Typography>
                                                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                                                        {String(value)}
                                                                    </Typography>
                                                                </Paper>
                                                            </Grid>
                                                        );
                                                    })}
                                                </Grid>
                                                {candidate.callMetadata && (
                                                    <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                                                        <Typography variant="caption" color="text.secondary">
                                                            Call ID: {candidate.callMetadata.callId || 'N/A'} |
                                                            Duration: {candidate.callMetadata.callDuration ? `${Math.round(candidate.callMetadata.callDuration / 60)} minutes` : 'N/A'} |
                                                            Ended: {candidate.callMetadata.callEndedAt ? new Date(candidate.callMetadata.callEndedAt).toLocaleString() : 'N/A'}
                                                        </Typography>

                                                        {/* Audio Recording Player */}
                                                        <Box sx={{ mt: 2 }}>
                                                            <Typography variant="subtitle2" sx={{ mb: 1, color: 'primary.main' }}>
                                                                🎧 Call Recording
                                                            </Typography>
                                                            {candidate.callMetadata.recordingUrl ? (
                                                                <audio
                                                                    controls
                                                                    style={{ width: '100%', maxWidth: 500 }}
                                                                    src={candidate.callMetadata.recordingUrl}
                                                                >
                                                                    Your browser does not support the audio element.
                                                                </audio>
                                                            ) : (
                                                                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                                                    No recording available for this interview.
                                                                </Typography>
                                                            )}
                                                        </Box>

                                                        {/* Transcript Chat */}
                                                        <Box sx={{ mt: 3 }}>
                                                            <Typography variant="subtitle2" sx={{ mb: 1.5, color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <ChatIcon sx={{ fontSize: 18 }} /> Interview Transcript
                                                            </Typography>
                                                            <TranscriptChat
                                                                transcript={
                                                                    candidate.callMetadata?.transcript?.length > 0
                                                                        ? candidate.callMetadata.transcript
                                                                        : (candidate.interviewResults?.transcript || [])
                                                                }
                                                                candidateName={candidate.candidateName || 'Candidate'}
                                                            />
                                                        </Box>
                                                    </Box>
                                                )}
                                            </AccordionDetails>
                                        </Accordion>
                                    );
                                })}
                            </Stack>
                        )}
                    </CardContent>
                </Card>
            </Box>
        </Fade>
    );
}
